import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { cloneDeep } from "lodash";
import { useState } from "react";
import mixpanel from "mixpanel-browser";

import { CREATE_ORGANIZATION, GET_USER } from "../../api/gqlQueries";
import {
  EOrganizationType,
  EPermissionActions,
  IUserData,
  IUserParams,
} from "../../interfaces/interfaces";
import { EPermissions, hasPermission } from "../../utils/rolesAndPermissions";
import BaseRegistrationModal from "../modals/BaseRegistrationModal";
import { ReactComponent as Properties } from "../../img/Properties.svg";
import { RenderEmptyHomeView } from "../../utils/utils";
import { ReactComponent as Settings } from "../../img/settings.svg";
import Spinner from "../Spinner";
import { userIdVar } from "../apollo/LocalState";

import SettingsCard from "./SettingsCard";
import OrganizationForm from "./OrganizationForm";

interface CreateOrganizationModalProps {
  newOrganizationValue: string;
  setNewOrganizationValue: React.Dispatch<React.SetStateAction<string>>;
  typeOfOrganization: EOrganizationType;
  setTypeOfOrganization: React.Dispatch<
    React.SetStateAction<EOrganizationType>
  >;
  closeModal: () => void;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  closeModal,
  newOrganizationValue,
  setNewOrganizationValue,
  typeOfOrganization,
  setTypeOfOrganization,
}) => {
  const userId = useReactiveVar(userIdVar);
  const [createOrganization] = useMutation(CREATE_ORGANIZATION);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const organizationName = formData.get("organization-name") as string;
    const organizationType = formData.get(
      "organization-type"
    ) as EOrganizationType;
    createOrganization({
      update: (cache, { data }) => {
        if (!data) {
          // No need to update cache if no data returned.
          return;
        }
        const cachedData = cache.readQuery<IUserData, IUserParams>({
          query: GET_USER,
          variables: { userId },
        });
        if (!cachedData) {
          return;
        }
        const updatedData = cloneDeep(cachedData);
        updatedData.user.organizations.push(
          data.createOrganization.organization
        );
        cache.writeQuery({
          data: updatedData,
          query: GET_USER,
          variables: { userId },
        });
        closeModal();
      },
      variables: { organizationName, organizationType },
    });
  };
  return (
    <BaseRegistrationModal
      closeModalFn={closeModal}
      headerText="Create Organization"
      Image={<Settings className="organization-create-img" />}
    >
      <OrganizationForm
        closeModal={closeModal}
        newOrganizationValue={newOrganizationValue}
        setNewOrganizationValue={setNewOrganizationValue}
        typeOfOrganization={typeOfOrganization}
        setTypeOfOrganization={setTypeOfOrganization}
        handleSubmit={handleSubmit}
      />
    </BaseRegistrationModal>
  );
};

const Organizations: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [newOrganizationValue, setNewOrganizationValue] = useState("");
  const [typeOfOrganization, setTypeOfOrganization] =
    useState<EOrganizationType>(EOrganizationType.BCC_ORGANIZATION);
  const userId = useReactiveVar(userIdVar);
  const { loading: userLoad, data: userData } = useQuery<
    IUserData,
    IUserParams
  >(GET_USER, {
    variables: { userId },
  });
  if (userLoad) return <Spinner />;
  if (!userData) throw Error("No data was returned");
  const { organizations } = userData.user;
  const numOrganizations = organizations.length;

  return (
    <div className="dashboard-container">
      {showModal && (
        <CreateOrganizationModal
          newOrganizationValue={newOrganizationValue}
          setNewOrganizationValue={setNewOrganizationValue}
          typeOfOrganization={typeOfOrganization}
          setTypeOfOrganization={setTypeOfOrganization}
          closeModal={() => setShowModal(false)}
        />
      )}
      <div className="dashboard-container-inner">
        <div className="options">
          <div className="dashboard-name-container">
            <div className="options-name menu">Organizations</div>
            {hasPermission(
              userData.user.role,
              EPermissions.ORGANIZATIONS,
              EPermissionActions.CREATE
            ) && (
              <button
                className="dashboard-name-button"
                type="button"
                data-testid="create-organization-btn"
                onClick={() => setShowModal(true)}
              >
                + Create Organization
              </button>
            )}
          </div>
          {numOrganizations === 0 ? (
            <RenderEmptyHomeView
              icon={Properties}
              iconClassName="dashboard-empty-img"
              iconTitle="Empty Properties"
              componentTitle="No Organizations"
            />
          ) : (
            <div className="options-properties">
              {organizations.map((organization) => {
                return (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        organization.organizationType ===
                        EOrganizationType.PRO_VIEW_ORGANIZATION
                      ) {
                        mixpanel.track("Organization Navigate into", {
                          organization_id: organization.id,
                          organization_name: organization.name,
                          organization_type: organization.organizationType,
                          user_id: userId,
                        });
                      }
                    }}
                    key={organization.id}
                  >
                    <SettingsCard
                      link={`/${organization.id}/organization`}
                      numBuilding={
                        organization.organizationType ===
                        EOrganizationType.BCC_ORGANIZATION
                          ? organization.numBuildings
                          : undefined
                      }
                      settingName={organization.name}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Organizations;
